import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './reddit.css';

/* ─── Status config ──────────────────────────────────────────────────────── */

const STATUS_LABEL = {
  scheduled: 'Scheduled',
  overdue:   'Overdue',
  submitted: 'Submitted',
  live:      'Live',
  deleted:   'Deleted',
  archived:  'Archived',
  dws:       'Instant Deletion',
  cancelled: 'Cancelled',
};

const STATUS_CLS = {
  submitted: 'rh-badge-submitted',
  live:      'rh-badge-live',
  scheduled: 'rh-badge-scheduled',
  overdue:   'rh-badge-overdue',
  deleted:   'rh-badge-deleted',
  archived:  'rh-badge-archived',
  dws:       'rh-badge-dws',
  cancelled: 'rh-badge-cancelled',
};

const STATUS_TOOLTIPS = {
  scheduled: 'The comment is planned to be posted at a selected date.',
  overdue:   'The scheduled posting time has passed, but the comment has not been posted yet.',
  submitted: 'The comment has been submitted to Reddit.',
  live:      'The comment is visible and live on Reddit.',
  deleted:   'The comment was removed from Reddit.',
  archived:  'This comment is archived and no longer active.',
  dws:       'The comment was deleted immediately after posting.',
  cancelled: 'This assignment was cancelled and was never posted.',
};

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function RhTooltip({ tip, children }) {
  const [coords, setCoords] = useState(null);
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setCoords({ x: r.left + r.width / 2, y: r.top });
      }}
      onMouseLeave={() => setCoords(null)}
    >
      {children}
      {coords && (
        <span style={{
          position: 'fixed', left: coords.x, top: coords.y - 8,
          transform: 'translate(-50%, -100%)',
          background: '#1e293b', color: '#f8fafc',
          fontSize: 11, fontWeight: 500, lineHeight: 1.45,
          padding: '6px 10px', borderRadius: 7,
          whiteSpace: 'normal', maxWidth: 220, width: 'max-content',
          textAlign: 'center', pointerEvents: 'none', zIndex: 99999,
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        }}>
          {tip}
        </span>
      )}
    </span>
  );
}

function StatusBadge({ status, isOverdue }) {
  const displayStatus = (isOverdue && status === 'scheduled') ? 'overdue' : status;
  const cls = STATUS_CLS[displayStatus] || 'rh-badge-scheduled';
  return (
    <RhTooltip tip={STATUS_TOOLTIPS[displayStatus]}>
      <span className={`rh-badge ${cls}`}>{STATUS_LABEL[displayStatus] || displayStatus}</span>
    </RhTooltip>
  );
}

function fmt(dateStr) {
  if (!dateStr) return '—';
  return dateStr.slice(0, 10);
}

function fmtTime(date) {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/* ─── Status options ──────────────────────────────────────────────────────── */

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'overdue',   label: 'Overdue' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'live',      label: 'Live' },
  { value: 'archived',  label: 'Archived' },
  { value: 'deleted',   label: 'Deleted' },
  { value: 'dws',       label: 'Instant Deletion' },
];

const DEFAULT_ACTIVE = new Set(['scheduled', 'overdue', 'submitted', 'live', 'archived']);

/* ─── Main component ─────────────────────────────────────────────────────── */

export default function ShareableReport() {
  const { token } = useParams();

  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [notFound, setNotFound]     = useState(false);
  const [error, setError]           = useState(null);
  const [loadedAt, setLoadedAt]     = useState(null);
  const [viewTarget, setViewTarget] = useState(null);

  const [activeStatuses, setActiveStatuses] = useState(() => new Set(DEFAULT_ACTIVE));
  const [statusDropOpen, setStatusDropOpen] = useState(false);
  const statusDropRef = useRef(null);

  useEffect(() => {
    function onDown(e) {
      if (statusDropRef.current && !statusDropRef.current.contains(e.target)) {
        setStatusDropOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  function toggleStatus(val) {
    setActiveStatuses(prev => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val); else next.add(val);
      return next;
    });
  }

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setNotFound(false);
    setError(null);
    axios.get(`/api/reddit/public/campaign-report/${token}/`)
      .then(({ data: d }) => { setData(d); setLoadedAt(new Date()); })
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
        else setError(err.response?.data?.detail || 'Failed to load report. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const comments     = data?.comments || [];
  const clientLabel  = data?.client_name || '—';
  const today        = new Date().toISOString().slice(0, 10);
  const liveCount    = comments.filter(c => c.status === 'live').length;

  const visibleComments = comments.filter((c) => {
    const isOverdue = c.status === 'scheduled' && c.publishing_date && c.publishing_date < today;
    const effectiveStatus = isOverdue ? 'overdue' : c.status;
    return activeStatuses.has(effectiveStatus);
  });

  /* ── Loading / error states ─────────────────────────────────────────────── */

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>Loading report…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', maxWidth: 420, padding: '0 24px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
          <h2 style={{ margin: '0 0 8px', color: '#1e293b' }}>Report not found</h2>
          <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.6 }}>
            This link is invalid or has been deactivated. Please contact your account manager for an updated link.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', maxWidth: 420, padding: '0 24px' }}>
          <h2 style={{ margin: '0 0 8px', color: '#1e293b' }}>Something went wrong</h2>
          <p style={{ color: '#64748b', fontSize: 14 }}>{error}</p>
        </div>
      </div>
    );
  }

  /* ── Main report ──────────────────────────────────────────────────────────── */

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* ── Top bar ────────────────────────────────────────────────────────── */}
      <header style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 100 }}>
        <div className="sr-header-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, overflow: 'hidden' }}>
            <span className="sr-title" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{clientLabel}</span>
            <span style={{ fontSize: 17, fontWeight: 400, color: '#94a3b8', margin: '0 2px', flexShrink: 0 }}>—</span>
            <span className="sr-subtitle" style={{ flexShrink: 0 }}>Overview</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
            {loadedAt && (
              <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                Updated {fmtTime(loadedAt)}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* ── Page body ──────────────────────────────────────────────────────── */}
      <main style={{ padding: '0 0 48px' }}>

        {/* Tab bar — content capped at 1400px */}
        <div>
        <div className="sr-bar-inner">
          {/* Tab */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginRight: 24,
              cursor: 'default',
            }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>All Assignments</span>
              <span style={{
                background: 'var(--rh-orange)', color: '#fff',
                borderRadius: 999, fontSize: 11, fontWeight: 700,
                padding: '1px 8px', lineHeight: '18px',
              }}>
                {visibleComments.length}
              </span>
            </div>
          </div>

          {/* Status filter */}
          <div ref={statusDropRef} style={{ position: 'relative', paddingBottom: 8, paddingTop: 8 }}>
            <button
              onClick={() => setStatusDropOpen(o => !o)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                border: `1px solid ${statusDropOpen ? 'var(--rh-orange)' : '#e2e8f0'}`,
                borderRadius: 8, padding: '5px 12px', fontSize: 13, fontWeight: 500,
                background: '#fff', cursor: 'pointer', color: '#475569',
                boxShadow: statusDropOpen ? '0 0 0 3px rgba(255,69,0,0.1)' : 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ flex: 1, textAlign: 'left' }}>
                {activeStatuses.size === 0
                  ? 'No Statuses'
                  : activeStatuses.size === STATUS_OPTIONS.length
                  ? 'All Statuses'
                  : `${activeStatuses.size} Selected`}
              </span>
              <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <polyline points={statusDropOpen ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}/>
              </svg>
            </button>

            {statusDropOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                background: '#fff', border: '1px solid #e2e8f0',
                borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                zIndex: 200, minWidth: 200, padding: '8px 0',
              }}>
                <div style={{ display: 'flex', gap: 6, padding: '4px 12px 8px', borderBottom: '1px solid #f1f5f9' }}>
                  <button onClick={() => setActiveStatuses(new Set(STATUS_OPTIONS.map(o => o.value)))}
                    style={{ fontSize: 11, fontWeight: 600, color: 'var(--rh-orange)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    All
                  </button>
                  <span style={{ color: '#cbd5e1' }}>·</span>
                  <button onClick={() => setActiveStatuses(new Set())}
                    style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    Clear
                  </button>
                  <span style={{ color: '#cbd5e1' }}>·</span>
                  <button onClick={() => setActiveStatuses(new Set(DEFAULT_ACTIVE))}
                    style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    Reset
                  </button>
                </div>
                {STATUS_OPTIONS.map(opt => (
                  <label key={opt.value} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: '#334155',
                    background: activeStatuses.has(opt.value) ? 'rgba(255,69,0,0.04)' : 'transparent',
                  }}>
                    <input
                      type="checkbox"
                      checked={activeStatuses.has(opt.value)}
                      onChange={() => toggleStatus(opt.value)}
                      style={{ accentColor: 'var(--rh-orange)', width: 14, height: 14, cursor: 'pointer' }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>

        {/* Table — content capped at 1400px */}
        <div className="sr-table-outer">
          <div className="sr-table-card">
            {visibleComments.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                No comments match the current filters.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                    {['POST TITLE', 'SUBREDDIT', 'THREAD CREATED', 'COMMENT PREVIEW', 'COMMENT DATE', 'STATUS'].map((h, i) => (
                      <th key={h} style={{
                        padding: '11px 16px',
                        textAlign: (i === 2 || i === 4) ? 'center' : 'left',
                        fontSize: 11, fontWeight: 600,
                        color: '#94a3b8',
                        letterSpacing: '0.06em',
                        whiteSpace: 'nowrap',
                        background: '#fafafa',
                        minWidth: i === 3 ? 280 : i === 0 ? 180 : i === 5 ? 110 : 100,
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleComments.map((c, i) => {
                    const cIsOverdue = c.status === 'scheduled' && c.publishing_date && c.publishing_date < today;
                    const sub = c.subreddit
                      ? (c.subreddit.startsWith('r/') ? c.subreddit : `r/${c.subreddit}`)
                      : '—';
                    return (
                      <tr key={i} style={{
                        borderBottom: i < visibleComments.length - 1 ? '1px solid #f1f5f9' : 'none',
                        transition: 'background 0.1s',
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >

                        {/* POST TITLE */}
                        <td style={{ padding: '12px 16px', maxWidth: 240 }}>
                          {c.reddit_url ? (
                            <a
                              href={c.reddit_url}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                fontSize: 13, fontWeight: 500, color: 'var(--rh-orange)',
                                textDecoration: 'none', display: '-webkit-box',
                                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                              }}
                            >
                              {c.post_title || '—'}
                            </a>
                          ) : (
                            <span style={{
                              fontSize: 13, fontWeight: 500, color: '#334155',
                              display: '-webkit-box', WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical', overflow: 'hidden',
                            }}>
                              {c.post_title || '—'}
                            </span>
                          )}
                        </td>

                        {/* SUBREDDIT */}
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <span style={{ fontSize: 13, color: '#475569' }}>{sub}</span>
                        </td>

                        {/* THREAD CREATED */}
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', textAlign: 'center' }}>
                          <span style={{ fontSize: 13, color: '#475569' }}>{fmt(c.post_date)}</span>
                        </td>

                        {/* COMMENT PREVIEW */}
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{
                              flex: 1, fontSize: 13, color: '#64748b', lineHeight: 1.5,
                              display: '-webkit-box', WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical', overflow: 'hidden',
                              minWidth: 0,
                            }}>
                              {c.comment_text || '—'}
                            </span>
                            <button
                              onClick={() => setViewTarget(c)}
                              style={{
                                flexShrink: 0,
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                fontSize: 12, fontWeight: 600,
                                color: 'var(--rh-orange)',
                                background: 'rgba(255,69,0,0.07)',
                                border: '1px solid rgba(255,69,0,0.18)',
                                borderRadius: 6, padding: '4px 10px',
                                cursor: 'pointer', whiteSpace: 'nowrap',
                              }}
                            >
                              View full
                              <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <polyline points="9 18 15 12 9 6"/>
                              </svg>
                            </button>
                          </div>
                        </td>

                        {/* COMMENT DATE */}
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', textAlign: 'center' }}>
                          <span style={{ fontSize: 13, color: '#475569' }}>{fmt(c.publishing_date)}</span>
                        </td>

                        {/* STATUS */}
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <StatusBadge status={c.status} isOverdue={cIsOverdue} />
                            {c.submitted_url && !['deleted', 'dws'].includes(c.status) && (
                              <a
                                href={c.submitted_url}
                                target="_blank"
                                rel="noreferrer"
                                title="View on Reddit"
                                style={{ color: 'var(--rh-orange)', display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}
                              >
                                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                  <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                                </svg>
                              </a>
                            )}
                          </div>
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      {/* ── Comment detail modal ────────────────────────────────────────────── */}
      {viewTarget && (
        <div
          className="rh-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setViewTarget(null)}
        >
          <div className="rh-modal" style={{ maxWidth: 580, width: '95%' }}>

            <div className="rh-modal-header">
              <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
                <h3 className="rh-modal-title" style={{ margin: 0, lineHeight: 1.4 }}>
                  {viewTarget.post_title || '—'}
                </h3>
                {viewTarget.subreddit && (
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--rh-text-muted)' }}>
                    {viewTarget.subreddit.startsWith('r/') ? viewTarget.subreddit : `r/${viewTarget.subreddit}`}
                  </p>
                )}
              </div>
              <button className="rh-modal-close" onClick={() => setViewTarget(null)} aria-label="Close">
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="rh-modal-body" style={{ padding: '20px 28px 24px' }}>

              {viewTarget.reddit_url && (
                <div style={{ marginBottom: 18 }}>
                  <p style={{ margin: '0 0 5px', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--rh-text-faint)' }}>
                    Post URL
                  </p>
                  <a
                    href={viewTarget.reddit_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 13, color: 'var(--rh-orange)', wordBreak: 'break-all', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                  >
                    {viewTarget.reddit_url}
                    <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </a>
                </div>
              )}

              <div style={{ marginBottom: viewTarget.submitted_url ? 20 : 0 }}>
                <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--rh-text-faint)' }}>
                  Comment
                </p>
                <div style={{
                  background: '#f9fafb', border: '1px solid var(--rh-border)',
                  borderRadius: 8, padding: '14px 16px',
                  fontSize: 13, color: 'var(--rh-text)', lineHeight: 1.7,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  maxHeight: 320, overflowY: 'auto',
                }}>
                  {viewTarget.comment_text || '—'}
                </div>
              </div>

              {viewTarget.submitted_url && !['deleted', 'dws'].includes(viewTarget.status) && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  flexWrap: 'wrap', gap: 12, marginTop: 18,
                  background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.2)',
                  borderRadius: 8, padding: '12px 16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      fontSize: 11, fontWeight: 700, color: '#16a34a',
                      background: 'rgba(22,163,74,0.12)', borderRadius: 20, padding: '2px 9px',
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                      {viewTarget.status === 'live' ? 'Live' : 'Submitted'}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--rh-text-muted)' }}>Comment is live on Reddit</span>
                  </div>
                  <a
                    href={viewTarget.submitted_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#16a34a', textDecoration: 'none', flexShrink: 0 }}
                  >
                    View on Reddit
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </a>
                </div>
              )}
            </div>

            <div className="rh-modal-footer">
              <button className="rh-btn rh-btn-secondary" onClick={() => setViewTarget(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
